import { useEffect, useState } from "react";
import { Routes, Route, NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { LayoutDashboard, Package, Users, ShoppingBag, Plus, Edit, Trash2, Coins, Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle, Star, Eye, FileText, RefreshCcw, Truck, X, ExternalLink, Tags, MessageSquare, ArrowUp, ArrowDown, GripVertical, Save } from "lucide-react";
import FileUploader from "../components/FileUploader";

const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

function AdminSidebar() {
  const items = [
    { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
    { to: "/admin/gold-rate", icon: Coins, label: "Gold Rate" },
    { to: "/admin/categories", icon: Tags, label: "Categories" },
    { to: "/admin/products", icon: Package, label: "Products" },
    { to: "/admin/bulk-upload", icon: FileSpreadsheet, label: "Bulk Upload" },
    { to: "/admin/home-carousel", icon: Star, label: "Home Carousel" },
    { to: "/admin/orders", icon: ShoppingBag, label: "Orders" },
    { to: "/admin/reviews", icon: MessageSquare, label: "Reviews" },
    { to: "/admin/users", icon: Users, label: "Users" },
  ];
  return (
    <aside className="lg:w-60 shrink-0" data-testid="admin-sidebar">
      <div className="bg-white border border-[#EAE3D9] rounded-2xl p-3 sticky top-32">
        {items.map(({ to, icon: I, label, end }) => (
          <NavLink key={to} to={to} end={end}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-colors ${isActive ? "bg-[#FFF5F3] text-[#8B1E28] font-semibold" : "text-[#595552] hover:bg-[#FDFBF7]"}`}
            data-testid={`admin-nav-${label.toLowerCase()}`}>
            <I className="h-4 w-4" /> {label}
          </NavLink>
        ))}
      </div>
    </aside>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { axios.get(`${API}/admin/stats`).then(r => setStats(r.data)); }, []);
  if (!stats) return <div className="text-[#8A8480]">Loading…</div>;
  const cards = [
    { label: "Revenue", value: inr(stats.revenue), color: "from-[#FFF5F3] to-white" },
    { label: "Orders", value: stats.orders, color: "from-[#FDFBF7] to-white" },
    { label: "Products", value: stats.products, color: "from-[#FFF5F3] to-white" },
    { label: "Users", value: stats.users, color: "from-[#FDFBF7] to-white" },
  ];
  return (
    <div data-testid="admin-dashboard">
      <h2 className="font-display text-3xl text-[#2C2A29] mb-6">Overview</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(c => (
          <div key={c.label} className={`bg-gradient-to-br ${c.color} border border-[#EAE3D9] rounded-2xl p-6`} data-testid={`stat-${c.label.toLowerCase()}`}>
            <div className="label-small text-[#8A8480]">{c.label}</div>
            <div className="font-display text-3xl text-[#2C2A29] mt-2">{c.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const EMPTY_PRODUCT = {
  name: "", category: "rings", description: "",
  purity: "22k", weight: 0,
  sizes: [], images: [""], featured: false, collection: "Daily Wear", stock: 10,
  stone_charges: 0, hide_price_breakup: false, hide_weight: false,
  is_diamond: false, diamond_weight_cts: 0, diamond_mrp: 0, diamond_discount_pct: 0,
};

function GoldRateAdmin() {
  const [rate, setRate] = useState(null);
  const [input, setInput] = useState("");
  const [silverInput, setSilverInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [savingSilver, setSavingSilver] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = () => axios.get(`${API}/gold-rate`).then(r => {
    setRate(r.data);
    setInput(String(r.data.rate_24k_per_gram));
    setSilverInput(String(r.data.silver_rate_per_gram ?? ""));
  });
  useEffect(() => { load(); }, []);

  const syncNow = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/admin/gold-rate/sync-now`);
      toast.success("Live rates pulled from source");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Live rate sync failed");
    } finally { setSyncing(false); }
  };

  const save = async () => {
    const n = parseFloat(input);
    if (isNaN(n) || n <= 0) { toast.error("Enter a valid rate"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/gold-rate`, { rate_24k_per_gram: n });
      toast.success(`Gold rate updated to ₹${n.toLocaleString("en-IN")}/g (24k)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  };

  const saveSilver = async () => {
    const n = parseFloat(silverInput);
    if (isNaN(n) || n <= 0) { toast.error("Enter a valid silver rate"); return; }
    setSavingSilver(true);
    try {
      await axios.put(`${API}/admin/gold-rate`, { silver_rate_per_gram: n });
      toast.success(`Silver rate updated to ₹${n.toLocaleString("en-IN")}/g`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally { setSavingSilver(false); }
  };

  if (!rate) return <div className="text-[#8A8480]">Loading…</div>;

  const ts = rate.updated_at ? new Date(rate.updated_at).toLocaleString("en-IN") : "—";

  return (
    <div data-testid="admin-gold-rate">
      <h2 className="font-display text-3xl text-[#2C2A29] mb-2">Daily Gold &amp; Silver Rates</h2>
      <p className="text-sm text-[#8A8480] mb-4">Rates auto-sync from a live source every 30 min. You can also fine-tune manually — your override sticks until the next sync.</p>

      <div className="flex flex-wrap items-center gap-3 mb-6 p-3 rounded-xl bg-emerald-50 border border-emerald-200" data-testid="rate-sync-status">
        <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
        <div className="text-xs text-emerald-800 flex-1">
          <span className="font-semibold">Auto-sync ON</span>
          {rate.auto_rate_last_synced && (
            <> · last synced {new Date(rate.auto_rate_last_synced).toLocaleString("en-IN")}</>
          )}
        </div>
        <Button onClick={syncNow} disabled={syncing} size="sm" variant="outline"
          className="border-emerald-300 text-emerald-800 hover:bg-emerald-100" data-testid="sync-now-btn">
          <RefreshCcw className={`h-3.5 w-3.5 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Syncing…" : "Sync now"}
        </Button>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-6">
          {/* Gold Rate Card */}
          <div className="bg-white border border-[#EAE3D9] rounded-2xl p-6">
            <Label className="label-small text-[#8A8480]">24k Gold Rate (₹ per gram)</Label>
            <div className="flex gap-3 mt-2">
              <Input type="number" value={input} onChange={e => setInput(e.target.value)}
                className="text-2xl font-semibold h-14" data-testid="gold-rate-input" />
              <Button onClick={save} disabled={saving} className="btn-primary px-6 h-14 rounded-xl" data-testid="gold-rate-save">
                {saving ? "Saving…" : "Update rate"}
              </Button>
            </div>
            <p className="text-xs text-[#8A8480] mt-3">Last updated: {ts}</p>

            <div className="grid grid-cols-3 gap-3 mt-6">
              <div className="bg-[#FFF5F3] rounded-xl p-4">
                <div className="label-small text-[#8A8480]">24k</div>
                <div className="font-display text-2xl text-[#2C2A29] mt-1">₹{rate.rate_24k_per_gram.toLocaleString("en-IN")}</div>
                <div className="text-xs text-[#8A8480]">per gram</div>
              </div>
              <div className="bg-[#FFF5F3] rounded-xl p-4">
                <div className="label-small text-[#8A8480]">22k (0.916)</div>
                <div className="font-display text-2xl text-[#2C2A29] mt-1">₹{rate.rate_22k_per_gram.toLocaleString("en-IN")}</div>
                <div className="text-xs text-[#8A8480]">per gram</div>
              </div>
              <div className="bg-[#FFF5F3] rounded-xl p-4">
                <div className="label-small text-[#8A8480]">18k (0.750)</div>
                <div className="font-display text-2xl text-[#2C2A29] mt-1">₹{rate.rate_18k_per_gram.toLocaleString("en-IN")}</div>
                <div className="text-xs text-[#8A8480]">per gram</div>
              </div>
            </div>
          </div>

          {/* Silver Rate Card */}
          <div className="bg-white border border-[#EAE3D9] rounded-2xl p-6" data-testid="silver-rate-card">
            <Label className="label-small text-[#8A8480]">Silver Rate (₹ per gram)</Label>
            <div className="flex gap-3 mt-2">
              <Input type="number" value={silverInput} onChange={e => setSilverInput(e.target.value)}
                className="text-2xl font-semibold h-14" data-testid="silver-rate-input"
                placeholder="e.g. 95" />
              <Button onClick={saveSilver} disabled={savingSilver}
                className="btn-primary px-6 h-14 rounded-xl" data-testid="silver-rate-save">
                {savingSilver ? "Saving…" : "Update rate"}
              </Button>
            </div>
            <p className="text-xs text-[#8A8480] mt-3">
              Current: <span className="font-semibold text-[#2C2A29]">₹{Number(rate.silver_rate_per_gram || 0).toLocaleString("en-IN")}/g</span>
            </p>
            <div className="mt-4 p-3 rounded-xl bg-[#FDFBF7] border border-[#EAE3D9] text-xs text-[#595552]">
              <span className="font-semibold text-[#8B1E28]">Silver pricing formula:</span> weight × silver rate + 30% making + 3% GST
            </div>
          </div>
        </div>

        <div className="bg-[#FFF5F3]/50 border border-[#EAE3D9] rounded-2xl p-6">
          <h3 className="font-display text-xl text-[#2C2A29] mb-3">Making charges logic (Gold)</h3>
          <ul className="text-xs text-[#595552] space-y-1.5">
            <li>≤ 0.5g → ₹1,500/pc</li>
            <li>≤ 0.8g → ₹2,000/pc</li>
            <li>≤ 1.0g → ₹2,500/pc</li>
            <li>≤ 1.5g → ₹3,000/pc</li>
            <li>&lt; 2.0g → 25% of gold value</li>
            <li>&lt; 3.5g → 20% of gold value</li>
            <li>&lt; 5.0g → 18% of gold value</li>
            <li>≥ 20g Chains → 15% of gold value</li>
            <li>otherwise → 20% of gold value</li>
            <li className="pt-2 border-t border-[#EAE3D9] mt-2">+ 3% GST applied on subtotal</li>
            <li>+ Optional stone charges (per product)</li>
          </ul>
          <h3 className="font-display text-xl text-[#2C2A29] mt-6 mb-3">Silver logic</h3>
          <ul className="text-xs text-[#595552] space-y-1.5">
            <li>Metal value = weight × silver rate</li>
            <li>Making = 30% of metal value</li>
            <li>+ 3% GST on subtotal</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function ProductsAdmin() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  // Reorder mode: when ON, admin can shuffle products up/down and save the new
  // `sort_order` — that same order drives the storefront (Home + /products + hover megamenu).
  const [reorderMode, setReorderMode] = useState(false);
  const [dirtyOrder, setDirtyOrder] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");

  const load = () => axios.get(`${API}/products?limit=500&sort=featured`).then(r => setProducts(r.data?.items || []));
  const loadCategories = () => axios.get(`${API}/categories`).then(r => setCategories(r.data || []));
  useEffect(() => { load(); loadCategories(); }, []);

  const moveRow = (index, dir) => {
    setProducts(prev => {
      const next = [...prev];
      const swap = index + dir;
      if (swap < 0 || swap >= next.length) return prev;
      [next[index], next[swap]] = [next[swap], next[index]];
      return next;
    });
    setDirtyOrder(true);
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      const ordered_ids = products.map(p => p.product_id);
      await axios.post(`${API}/admin/products/reorder`, { ordered_ids });
      toast.success("Display order saved");
      setDirtyOrder(false);
      setReorderMode(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save order");
    } finally {
      setSavingOrder(false);
    }
  };

  const filteredProducts = categoryFilter === "all"
    ? products
    : products.filter(p => p.category === categoryFilter);

  const openNew = () => { setEditing(null); setForm(EMPTY_PRODUCT); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p.product_id);
    setForm({ ...p, sizes: p.sizes || [], images: p.images || [""] });
    setOpen(true);
  };
  const save = async () => {
    const payload = {
      ...form,
      weight: Number(form.weight),
      stone_charges: Number(form.stone_charges) || 0,
      stock: Number(form.stock) || 10,
      price: 0, mrp: 0,  // computed live on backend
      images: form.images.filter(i => i.trim()),
      sizes: typeof form.sizes === "string" ? form.sizes.split(",").map(s => s.trim()).filter(Boolean) : form.sizes,
      is_diamond: !!form.is_diamond,
      diamond_weight_cts: Number(form.diamond_weight_cts) || 0,
      diamond_mrp: Number(form.diamond_mrp) || 0,
      diamond_discount_pct: Number(form.diamond_discount_pct) || 0,
    };
    // Diamond pieces are always 18k — enforce here so the admin can't accidentally
    // ship a 22k+diamond mismatch to the storefront.
    if (payload.is_diamond) payload.purity = "18k";
    try {
      if (editing) await axios.put(`${API}/products/${editing}`, payload);
      else await axios.post(`${API}/products`, payload);
      toast.success("Saved");
      setOpen(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await axios.delete(`${API}/products/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="font-display text-3xl text-[#2C2A29]">Products</h2>
        <div className="flex items-center gap-2 flex-wrap">
          {reorderMode && dirtyOrder && (
            <Button onClick={saveOrder} disabled={savingOrder} className="btn-primary rounded-full" data-testid="save-order-btn">
              <Save className="h-4 w-4 mr-2" /> {savingOrder ? "Saving…" : "Save display order"}
            </Button>
          )}
          <Button
            onClick={() => { setReorderMode(m => !m); setDirtyOrder(false); }}
            variant="outline"
            className={`rounded-full ${reorderMode ? "border-[#8B1E28] text-[#8B1E28] bg-[#FFF5F3]" : "border-[#EAE3D9] text-[#595552]"}`}
            data-testid="toggle-reorder-mode"
          >
            <GripVertical className="h-4 w-4 mr-2" /> {reorderMode ? "Exit reorder" : "Reorder"}
          </Button>
          <Button onClick={openNew} className="btn-primary rounded-full" data-testid="admin-add-product">
            <Plus className="h-4 w-4 mr-2" /> Add product
          </Button>
        </div>
      </div>

      {reorderMode && (
        <div className="mb-4 p-4 bg-[#FFF5F3] border border-[#F1D9D4] rounded-2xl text-sm text-[#595552] flex items-center justify-between flex-wrap gap-3">
          <div>
            <strong className="text-[#8B1E28]">Reorder mode is on.</strong> Use the arrows to move products up or down.
            Items higher in this list appear first everywhere — home page, /products grid, and the megamenu preview.
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="text-sm bg-white border border-[#EAE3D9] rounded-full px-3 py-1.5 focus:outline-none focus:border-[#C5A059]"
            data-testid="reorder-category-filter"
          >
            <option value="all">All categories</option>
            {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
        </div>
      )}

      <div className="bg-white border border-[#EAE3D9] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#FDFBF7] text-[#8A8480]">
            <tr>
              {reorderMode && <th className="text-left px-3 py-3 font-medium w-24">Order</th>}
              <th className="text-left px-4 py-3 font-medium">Product</th>
              <th className="text-left px-4 py-3 font-medium">Category</th>
              <th className="text-left px-4 py-3 font-medium">Purity · Weight</th>
              <th className="text-right px-4 py-3 font-medium">Live Price</th>
              <th className="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.map((p) => {
              const gi = products.findIndex(x => x.product_id === p.product_id);
              return (
              <tr key={p.product_id} className="border-t border-[#EAE3D9]" data-testid={`row-${p.product_id}`}>
                {reorderMode && (
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => moveRow(gi, -1)}
                        disabled={gi <= 0}
                        className="p-1.5 rounded-md border border-[#EAE3D9] hover:border-[#C5A059] text-[#595552] disabled:opacity-30"
                        title="Move up"
                        data-testid={`row-up-${p.product_id}`}
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => moveRow(gi, 1)}
                        disabled={gi >= products.length - 1}
                        className="p-1.5 rounded-md border border-[#EAE3D9] hover:border-[#C5A059] text-[#595552] disabled:opacity-30"
                        title="Move down"
                        data-testid={`row-down-${p.product_id}`}
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </button>
                      <span className="ml-1 text-[10px] text-[#8A8480]">#{gi + 1}</span>
                    </div>
                  </td>
                )}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <img src={p.images?.[0]} alt="" className="h-10 w-10 rounded-lg object-cover bg-[#FFF5F3]" />
                    <div className="line-clamp-1 max-w-[260px]">{p.name}</div>
                  </div>
                </td>
                <td className="px-4 py-3 capitalize">{p.category}</td>
                <td className="px-4 py-3 text-xs text-[#595552]">{p.purity} · {p.weight}g</td>
                <td className="px-4 py-3 text-right">{inr(p.price)}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEdit(p)} className="text-[#595552] hover:text-[#8B1E28] mr-3" data-testid={`edit-${p.product_id}`}>
                    <Edit className="h-4 w-4 inline" />
                  </button>
                  <button onClick={() => del(p.product_id)} className="text-[#595552] hover:text-[#D32F2F]" data-testid={`delete-${p.product_id}`}>
                    <Trash2 className="h-4 w-4 inline" />
                  </button>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Edit product" : "Add product"}</DialogTitle>
            <DialogDescription className="text-[#8A8480] text-sm">
              Fill in the details below. Price is calculated live from the daily gold rate × weight × purity — you don&apos;t need to enter it manually.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => (
                      <SelectItem key={c.slug} value={c.slug} data-testid={`opt-cat-${c.slug}`}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Purity</Label>
                <Select value={form.purity} onValueChange={(v) => setForm({ ...form, purity: v })}>
                  <SelectTrigger data-testid="form-purity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="18k">18k</SelectItem>
                    <SelectItem value="22k">22k</SelectItem>
                    <SelectItem value="Silver">Silver</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{form.is_diamond ? "Net gold weight (g) *" : "Weight (g) *"}</Label>
                <Input type="number" step="0.001" value={form.weight} onChange={e => setForm({ ...form, weight: e.target.value })} data-testid="form-weight" />
              </div>
              <div>
                <Label>Stone charges (₹, optional)</Label>
                <Input type="number" value={form.stone_charges} onChange={e => setForm({ ...form, stone_charges: e.target.value })} data-testid="form-stone-charges" />
              </div>
            </div>

            {/* Diamond toggle — pops open the 3 diamond-specific inputs (weight, MRP, discount).
                Making charges reuse the same 18k tier logic as regular gold. */}
            <div className="border border-[#EAE3D9] rounded-xl p-3 bg-[#FFFDF9]">
              <label className="flex items-center gap-2 text-sm font-medium text-[#2C2A29] cursor-pointer" title="Turn on for diamond pieces. Locks purity to 18k and unlocks the diamond fields.">
                <input
                  type="checkbox"
                  checked={!!form.is_diamond}
                  onChange={e => setForm({
                    ...form,
                    is_diamond: e.target.checked,
                    purity: e.target.checked ? "18k" : form.purity,
                  })}
                  data-testid="form-is-diamond"
                />
                This is a diamond piece (always 18k gold)
              </label>
              {form.is_diamond && (
                <div className="mt-3 grid grid-cols-3 gap-3">
                  <div>
                    <Label>Diamond weight (ct) *</Label>
                    <Input type="number" step="0.001" value={form.diamond_weight_cts}
                      onChange={e => setForm({ ...form, diamond_weight_cts: e.target.value })}
                      data-testid="form-diamond-cts" />
                  </div>
                  <div>
                    <Label>Diamond MRP (₹) *</Label>
                    <Input type="number" value={form.diamond_mrp}
                      onChange={e => setForm({ ...form, diamond_mrp: e.target.value })}
                      data-testid="form-diamond-mrp" />
                  </div>
                  <div>
                    <Label>Discount % on MRP</Label>
                    <Input type="number" step="0.1" min="0" max="100" value={form.diamond_discount_pct}
                      onChange={e => setForm({ ...form, diamond_discount_pct: e.target.value })}
                      data-testid="form-diamond-discount" />
                  </div>
                </div>
              )}
            </div>
            <div className="bg-[#FFF5F3]/60 border border-[#EAE3D9] rounded-lg p-3 text-xs text-[#595552]">
              <Coins className="inline h-3 w-3 mr-1 text-[#C5A059]" />
              Price is computed live from the daily 24k gold rate × purity × weight + making charges + stone charges + 3% GST. Set the daily rate under <strong>Admin → Gold Rate</strong>.
            </div>
            <div>
              <Label>Sizes (comma-separated, optional)</Label>
              <Input value={Array.isArray(form.sizes) ? form.sizes.join(", ") : form.sizes}
                onChange={e => setForm({ ...form, sizes: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                placeholder="6, 7, 8, 9" data-testid="form-sizes" />
            </div>
            <div>
              <Label>Product images & videos</Label>
              <FileUploader
                value={form.images}
                onChange={(images) => setForm({ ...form, images })}
              />
            </div>
            <div>
              <Label>Collection</Label>
              <Select value={form.collection} onValueChange={(v) => setForm({ ...form, collection: v })}>
                <SelectTrigger data-testid="form-collection"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Daily Wear">Daily Wear</SelectItem>
                  <SelectItem value="Festive">Festive</SelectItem>
                  <SelectItem value="Bridal">Bridal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} data-testid="form-description" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.featured}
                onChange={e => setForm({ ...form, featured: e.target.checked })}
                data-testid="form-featured" />
              Featured on home page
            </label>
            <label className="flex items-center gap-2 text-sm" title="If ON, storefront hides the live price breakdown and shows only the final total. Pricing math still runs the same on the backend.">
              <input type="checkbox" checked={!!form.hide_price_breakup}
                onChange={e => setForm({ ...form, hide_price_breakup: e.target.checked })}
                data-testid="form-hide-breakup" />
              Hide live price breakup on storefront (show only final price)
            </label>
            <label className="flex items-center gap-2 text-sm" title="If ON, storefront hides the gross weight in the specifications card. Weight is still used for pricing.">
              <input type="checkbox" checked={!!form.hide_weight}
                onChange={e => setForm({ ...form, hide_weight: e.target.checked })}
                data-testid="form-hide-weight" />
              Hide weight on storefront
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="btn-primary" data-testid="form-save">Save product</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BulkUploadAdmin() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/admin/products/bulk-template`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "sumanya-products-template.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to download template");
    }
  };

  const upload = async () => {
    if (!file) { toast.error("Choose a CSV file first"); return; }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/products/bulk-upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      if (r.data.inserted_count > 0) {
        toast.success(`${r.data.inserted_count} product(s) added`);
      }
      if (r.data.error_count > 0) {
        toast.warning(`${r.data.error_count} row(s) had errors — see report below`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="admin-bulk-upload" className="max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-3xl text-[#2C2A29]">Bulk Product Upload</h2>
      </div>

      <div className="bg-white border border-[#EAE3D9] rounded-2xl p-6 mb-6">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between mb-5">
          <div>
            <h3 className="font-semibold text-[#2C2A29] text-lg">Step 1 · Download the template</h3>
            <p className="text-sm text-[#8A8480] mt-1">
              Start with our CSV template. It has all required columns and 2 sample rows.
            </p>
          </div>
          <Button
            variant="outline"
            className="border-[#C5A059] text-[#8B1E28] hover:bg-[#FFF5F3] shrink-0"
            onClick={downloadTemplate}
            data-testid="download-template-btn"
          >
            <Download className="h-4 w-4 mr-2" /> Download Template
          </Button>
        </div>

        <div className="border-t border-[#EAE3D9] pt-5">
          <h3 className="font-semibold text-[#2C2A29] text-lg mb-2">Step 2 · Upload your file</h3>
          <p className="text-sm text-[#8A8480] mb-4">
            Fill in the template and re-upload. Prices are computed live from weight × gold rate — leave <code className="bg-[#FDFBF7] px-1 rounded">price</code> and <code className="bg-[#FDFBF7] px-1 rounded">mrp</code> as <code className="bg-[#FDFBF7] px-1 rounded">0</code>.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="csv-file-input"
              className="flex-1"
            />
            <Button
              onClick={upload}
              disabled={!file || uploading}
              className="bg-[#8B1E28] hover:bg-[#7A1A24] text-white shrink-0"
              data-testid="upload-csv-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              {uploading ? "Uploading…" : "Upload CSV"}
            </Button>
          </div>

          {file && (
            <p className="text-xs text-[#8A8480] mt-2" data-testid="selected-file-name">
              Selected: <span className="font-medium text-[#2C2A29]">{file.name}</span> · {(file.size / 1024).toFixed(1)} KB
            </p>
          )}
        </div>
      </div>

      <div className="bg-[#FDFBF7] border border-[#EAE3D9] rounded-2xl p-5 mb-6">
        <h3 className="font-semibold text-[#2C2A29] mb-2 text-sm">CSV columns</h3>
        <div className="text-xs text-[#595552] grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
          <div><span className="font-semibold text-[#8B1E28]">name*</span> — product title</div>
          <div><span className="font-semibold text-[#8B1E28]">category*</span> — rings / earrings / necklaces / bangles / mangalsutra / pendants / bracelets / chains / silver</div>
          <div><span className="font-semibold text-[#8B1E28]">purity*</span> — 18k / 22k / 24k / Silver</div>
          <div><span className="font-semibold text-[#8B1E28]">weight*</span> — grams (e.g. 5.2)</div>
          <div>description — free text</div>
          <div>making_charges_pct — e.g. 12</div>
          <div>stone_charges — flat ₹ amount</div>
          <div>collection — Bridal / Daily Wear / Festive</div>
          <div>sizes — pipe-separated e.g. <code>6|7|8</code></div>
          <div>images — pipe-separated URLs</div>
          <div>stock — integer, default 10</div>
          <div>featured — true / false</div>
        </div>
        <p className="text-[11px] text-[#8A8480] mt-3">* required fields</p>
      </div>

      {result && (
        <div data-testid="bulk-result">
          <div className="flex gap-4 mb-5">
            <div className="flex-1 bg-white border border-[#EAE3D9] rounded-2xl p-4 flex items-center gap-3">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              <div>
                <div className="text-2xl font-semibold text-[#2C2A29]" data-testid="inserted-count">{result.inserted_count}</div>
                <div className="text-xs text-[#8A8480]">products added</div>
              </div>
            </div>
            <div className="flex-1 bg-white border border-[#EAE3D9] rounded-2xl p-4 flex items-center gap-3">
              <AlertCircle className={`h-8 w-8 ${result.error_count > 0 ? "text-red-600" : "text-[#C5A059]"}`} />
              <div>
                <div className="text-2xl font-semibold text-[#2C2A29]" data-testid="error-count">{result.error_count}</div>
                <div className="text-xs text-[#8A8480]">rows skipped</div>
              </div>
            </div>
          </div>

          {result.errors?.length > 0 && (
            <div className="bg-white border border-red-100 rounded-2xl overflow-hidden">
              <div className="px-5 py-3 bg-red-50 border-b border-red-100 text-sm font-semibold text-red-800">
                Rows with errors
              </div>
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="text-[#8A8480] text-xs">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Row #</th>
                      <th className="text-left px-4 py-2 font-medium">Error</th>
                      <th className="text-left px-4 py-2 font-medium">Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.errors.map((e, i) => (
                      <tr key={i} className="border-t border-[#EAE3D9]" data-testid={`error-row-${e.row}`}>
                        <td className="px-4 py-2 font-mono text-xs">{e.row}</td>
                        <td className="px-4 py-2 text-red-700">{e.error}</td>
                        <td className="px-4 py-2 text-[#595552]">{e.raw?.name || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const ORDER_STATUS_OPTIONS = [
  { value: "pending", label: "Pending payment", tone: "bg-amber-50 text-amber-800 border-amber-200" },
  { value: "placed", label: "Placed (COD)", tone: "bg-sky-50 text-sky-800 border-sky-200" },
  { value: "paid", label: "Paid", tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  { value: "confirmed", label: "Confirmed", tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  { value: "packed", label: "Packed", tone: "bg-indigo-50 text-indigo-800 border-indigo-200" },
  { value: "shipped", label: "Shipped", tone: "bg-blue-50 text-blue-800 border-blue-200" },
  { value: "out_for_delivery", label: "Out for delivery", tone: "bg-blue-50 text-blue-800 border-blue-200" },
  { value: "delivered", label: "Delivered", tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  { value: "cancelled", label: "Cancelled", tone: "bg-red-50 text-red-800 border-red-200" },
  { value: "return_requested", label: "Return requested", tone: "bg-orange-50 text-orange-800 border-orange-200" },
  { value: "returned", label: "Returned", tone: "bg-orange-50 text-orange-800 border-orange-200" },
  { value: "exchange_requested", label: "Exchange requested", tone: "bg-purple-50 text-purple-800 border-purple-200" },
  { value: "exchanged", label: "Exchanged", tone: "bg-purple-50 text-purple-800 border-purple-200" },
  { value: "refunded", label: "Refunded", tone: "bg-rose-50 text-rose-800 border-rose-200" },
  { value: "payment_failed", label: "Payment failed", tone: "bg-red-50 text-red-800 border-red-200" },
];

const statusMeta = (s) => ORDER_STATUS_OPTIONS.find(o => o.value === s) || { label: s, tone: "bg-[#FDFBF7] text-[#595552] border-[#EAE3D9]" };

function OrderDetailDialog({ order, onClose, onSaved }) {
  const [status, setStatus] = useState(order.status);
  const [awb, setAwb] = useState(order.tracking_awb || "");
  const [carrier, setCarrier] = useState(order.shipping_carrier || "Shiprocket");
  const [notes, setNotes] = useState(order.admin_notes || "");
  const [saving, setSaving] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState(String(order.total));
  const [refundReason, setRefundReason] = useState("");
  const [refunding, setRefunding] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/orders/${order.order_id}/status`, {
        status,
        tracking_awb: awb,
        shipping_carrier: carrier,
        admin_notes: notes,
      });
      toast.success("Order updated");
      onSaved(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const downloadInvoice = async () => {
    try {
      const r = await axios.get(`${API}/admin/orders/${order.order_id}/invoice`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Sumanya-Invoice-${order.order_id}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Invoice downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invoice generation failed");
    }
  };

  const processRefund = async () => {
    const amt = parseFloat(refundAmount);
    if (isNaN(amt) || amt <= 0) { toast.error("Enter a valid refund amount"); return; }
    if (!window.confirm(`Refund ₹${amt.toLocaleString("en-IN")} for order ${order.order_id}?`)) return;
    setRefunding(true);
    try {
      const r = await axios.post(`${API}/admin/orders/${order.order_id}/refund`, {
        amount: amt, reason: refundReason,
      });
      toast.success("Refund processed");
      onSaved(r.data);
      setRefundOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refund failed");
    } finally {
      setRefunding(false);
    }
  };

  const addr = order.address || {};
  const meta = statusMeta(order.status);
  const trackingUrl = order.tracking_url || (awb ? `https://shiprocket.co/tracking/${awb}` : null);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="order-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-3">
            Order {order.order_id}
            <span className={`text-[11px] uppercase tracking-wider px-2 py-1 rounded-full border ${meta.tone}`}>{meta.label}</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-[#8A8480]">
            Placed on {new Date(order.created_at).toLocaleString("en-IN")} · {order.payment_method?.toUpperCase()}
            {order.payment_id ? ` · Payment ${order.payment_id}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Customer */}
          <div className="bg-[#FDFBF7] border border-[#EAE3D9] rounded-xl p-4 text-sm" data-testid="order-customer-block">
            <div className="label-small text-[#8A8480] mb-2">Customer &amp; Shipping</div>
            <div className="font-semibold text-[#2C2A29]">{addr.full_name}</div>
            <div className="text-[#595552]">{addr.phone}</div>
            <div className="text-[#595552] mt-2">
              {addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}<br />
              {addr.city}, {addr.state} - {addr.pincode}
            </div>
          </div>
          {/* Financials */}
          <div className="bg-[#FDFBF7] border border-[#EAE3D9] rounded-xl p-4 text-sm">
            <div className="label-small text-[#8A8480] mb-2">Financials</div>
            <div className="flex justify-between"><span>Items</span><span>{order.items?.length || 0}</span></div>
            <div className="flex justify-between"><span>Total</span><span className="font-semibold">{inr(order.total)}</span></div>
            <div className="flex justify-between"><span>Payment</span><span className="capitalize">{order.payment_method}</span></div>
            {order.refund_id && (
              <div className="flex justify-between text-rose-700 pt-2 border-t border-[#EAE3D9] mt-2">
                <span>Refunded</span><span>{inr(order.refund_amount || 0)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Items */}
        <div className="bg-white border border-[#EAE3D9] rounded-xl overflow-hidden mt-4" data-testid="order-items-block">
          <div className="px-4 py-2 bg-[#FDFBF7] border-b border-[#EAE3D9] text-xs font-semibold text-[#8A8480]">Items</div>
          {order.items?.map((it, i) => (
            <div key={i} className="flex items-center gap-3 p-3 border-b border-[#EAE3D9] last:border-b-0">
              <img src={it.image} alt="" className="h-12 w-12 rounded-lg object-cover bg-[#FFF5F3]" />
              <div className="flex-1 text-sm">
                <div className="font-medium text-[#2C2A29]">{it.name}</div>
                <div className="text-xs text-[#8A8480]">Qty {it.quantity} {it.size ? `· Size ${it.size}` : ""}</div>
              </div>
              <div className="text-sm font-semibold">{inr(it.price * it.quantity)}</div>
            </div>
          ))}
        </div>

        {/* Status & Tracking */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="detail-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ORDER_STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Shipping carrier</Label>
            <Input value={carrier} onChange={e => setCarrier(e.target.value)} placeholder="Shiprocket / BlueDart / DTDC…" data-testid="detail-carrier" />
          </div>
          <div className="md:col-span-2">
            <Label>AWB / Tracking number</Label>
            <div className="flex gap-2">
              <Input value={awb} onChange={e => setAwb(e.target.value)} placeholder="e.g. 12345678901" data-testid="detail-awb" />
              {trackingUrl && (
                <a href={trackingUrl} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 px-3 rounded-md border border-[#EAE3D9] text-xs text-[#8B1E28] hover:bg-[#FFF5F3]"
                   data-testid="detail-tracking-link">
                  <ExternalLink className="h-3.5 w-3.5" /> Track
                </a>
              )}
            </div>
            <p className="text-[11px] text-[#8A8480] mt-1">AWB will link to Shiprocket public tracking.</p>
          </div>
          <div className="md:col-span-2">
            <Label>Admin notes</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Internal notes…" data-testid="detail-notes" />
          </div>
        </div>

        {/* Status history */}
        {order.status_history?.length > 0 && (
          <div className="mt-4 bg-[#FDFBF7] border border-[#EAE3D9] rounded-xl p-4" data-testid="order-history-block">
            <div className="label-small text-[#8A8480] mb-2">Status timeline</div>
            <ol className="space-y-2 text-xs">
              {order.status_history.map((h, i) => (
                <li key={i} className="flex items-baseline gap-3">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#8B1E28] shrink-0" />
                  <span className="font-medium text-[#2C2A29] capitalize">{h.status.replaceAll("_"," ")}</span>
                  <span className="text-[#8A8480]">· {new Date(h.at).toLocaleString("en-IN")}</span>
                  <span className="text-[#8A8480]">· {h.by}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={downloadInvoice} data-testid="detail-invoice-btn">
            <FileText className="h-4 w-4 mr-2" /> Download Invoice
          </Button>
          {(order.payment_method === "razorpay" ? order.payment_id : true) && order.status !== "refunded" && (
            <Button variant="outline" onClick={() => setRefundOpen(v => !v)}
                    className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="detail-refund-btn">
              <RefreshCcw className="h-4 w-4 mr-2" /> {refundOpen ? "Cancel refund" : "Issue refund"}
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={save} disabled={saving} className="btn-primary" data-testid="detail-save-btn">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>

        {refundOpen && (
          <div className="mt-4 p-4 rounded-xl border border-rose-200 bg-rose-50/50" data-testid="refund-form">
            <div className="text-sm font-semibold text-rose-800 mb-3 flex items-center gap-2">
              <RefreshCcw className="h-4 w-4" /> Issue refund
              {order.payment_method === "razorpay" ? (
                <span className="text-[10px] uppercase font-medium px-2 py-0.5 rounded-full bg-white border border-rose-200 text-rose-700">Via Razorpay</span>
              ) : (
                <span className="text-[10px] uppercase font-medium px-2 py-0.5 rounded-full bg-white border border-rose-200 text-rose-700">Mark refunded (COD)</span>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Amount (₹)</Label>
                <Input type="number" value={refundAmount} onChange={e => setRefundAmount(e.target.value)} data-testid="refund-amount" />
              </div>
              <div>
                <Label className="text-xs">Reason</Label>
                <Input value={refundReason} onChange={e => setRefundReason(e.target.value)} placeholder="e.g. Customer request" data-testid="refund-reason" />
              </div>
            </div>
            <div className="flex justify-end mt-3">
              <Button onClick={processRefund} disabled={refunding}
                      className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="refund-confirm-btn">
                {refunding ? "Processing…" : `Refund ₹${Number(refundAmount || 0).toLocaleString("en-IN")}`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function OrdersAdmin() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const load = () => axios.get(`${API}/admin/orders`).then(r => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? orders : orders.filter(o => o.status === filter);
  const deletable = filtered.filter(o => o.status === "cancelled" || o.status === "payment_failed");
  const showBulkBar = filter === "cancelled" || filter === "payment_failed";

  const toggleOne = (id) => {
    setSelected(s => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    setSelected(s => s.size === deletable.length ? new Set() : new Set(deletable.map(o => o.order_id)));
  };
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} order(s)? This cannot be undone.`)) return;
    const r = await axios.post(`${API}/admin/orders/bulk-delete`, { order_ids: Array.from(selected) });
    toast.success(`Deleted ${r.data.deleted} order(s)`);
    setSelected(new Set());
    load();
  };

  const counts = orders.reduce((acc, o) => { acc[o.status] = (acc[o.status] || 0) + 1; return acc; }, {});

  const chips = [
    { value: "all", label: "All" },
    { value: "pending", label: "Pending" },
    { value: "paid", label: "Paid" },
    { value: "placed", label: "COD placed" },
    { value: "packed", label: "Packed" },
    { value: "shipped", label: "Shipped" },
    { value: "delivered", label: "Delivered" },
    { value: "return_requested", label: "Returns" },
    { value: "refunded", label: "Refunded" },
    { value: "cancelled", label: "Cancelled" },
  ];

  return (
    <div data-testid="admin-orders">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="font-display text-3xl text-[#2C2A29]">Orders</h2>
        <Button variant="outline" onClick={load} data-testid="orders-refresh">
          <RefreshCcw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-5" data-testid="order-filter-chips">
        {chips.map(c => {
          const active = filter === c.value;
          const n = c.value === "all" ? orders.length : (counts[c.value] || 0);
          return (
            <button key={c.value} onClick={() => { setFilter(c.value); setSelected(new Set()); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                active ? "bg-[#8B1E28] text-white border-[#8B1E28]" : "bg-white text-[#595552] border-[#EAE3D9] hover:border-[#C5A059]"
              }`}
              data-testid={`filter-${c.value}`}>
              {c.label} <span className={`ml-1.5 ${active ? "text-white/80" : "text-[#8A8480]"}`}>{n}</span>
            </button>
          );
        })}
      </div>

      {showBulkBar && deletable.length > 0 && (
        <div className="flex items-center justify-between mb-4 px-4 py-3 bg-[#FFF5F3] border border-[#EAE3D9] rounded-xl" data-testid="bulk-action-bar">
          <label className="flex items-center gap-2 text-sm text-[#2C2A29] cursor-pointer">
            <input type="checkbox"
              checked={selected.size === deletable.length && deletable.length > 0}
              onChange={toggleAll}
              data-testid="bulk-select-all" />
            {selected.size > 0 ? `${selected.size} of ${deletable.length} selected` : `Select all ${deletable.length}`}
          </label>
          <Button variant="outline" onClick={bulkDelete} disabled={selected.size === 0}
            className="text-[#D32F2F] border-[#D32F2F] hover:bg-[#FFF5F3]"
            data-testid="bulk-delete-btn">
            <Trash2 className="h-4 w-4 mr-2" /> Delete selected
          </Button>
        </div>
      )}

      <div className="bg-white border border-[#EAE3D9] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FDFBF7] text-[#8A8480]">
              <tr>
                {showBulkBar && <th className="w-10 px-4 py-3"></th>}
                <th className="text-left px-4 py-3 font-medium">Order</th>
                <th className="text-left px-4 py-3 font-medium">Customer</th>
                <th className="text-left px-4 py-3 font-medium">Date</th>
                <th className="text-right px-4 py-3 font-medium">Total</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-left px-4 py-3 font-medium">AWB</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={showBulkBar ? "8" : "7"} className="px-4 py-10 text-center text-[#8A8480]">No orders in this view</td></tr>
              )}
              {filtered.map(o => {
                const m = statusMeta(o.status);
                const isDeletable = o.status === "cancelled" || o.status === "payment_failed";
                return (
                  <tr key={o.order_id} className="border-t border-[#EAE3D9] hover:bg-[#FDFBF7]/40" data-testid={`order-row-${o.order_id}`}>
                    {showBulkBar && (
                      <td className="px-4 py-3">
                        {isDeletable && (
                          <input type="checkbox"
                            checked={selected.has(o.order_id)}
                            onChange={() => toggleOne(o.order_id)}
                            data-testid={`bulk-select-${o.order_id}`} />
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#2C2A29]">{o.order_id}</div>
                      <div className="text-xs text-[#8A8480] capitalize">{o.payment_method}</div>
                    </td>
                    <td className="px-4 py-3">
                      {o.address?.full_name}
                      <div className="text-xs text-[#8A8480]">{o.address?.city}, {o.address?.state}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#595552]">
                      {new Date(o.created_at).toLocaleDateString("en-IN")}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">{inr(o.total)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${m.tone}`}>
                        {m.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {o.tracking_awb ? (
                        <a href={o.tracking_url || `https://shiprocket.co/tracking/${o.tracking_awb}`} target="_blank" rel="noreferrer"
                           className="text-[#8B1E28] hover:underline inline-flex items-center gap-1" data-testid={`row-awb-${o.order_id}`}>
                          <Truck className="h-3.5 w-3.5" /> {o.tracking_awb}
                        </a>
                      ) : <span className="text-[#B8B0AA]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setDetail(o)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#EAE3D9] text-xs text-[#595552] hover:border-[#C5A059] hover:text-[#8B1E28]"
                        data-testid={`view-${o.order_id}`}>
                        <Eye className="h-3.5 w-3.5" /> View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {detail && (
        <OrderDetailDialog
          order={detail}
          onClose={() => setDetail(null)}
          onSaved={(updated) => {
            setOrders(orders.map(x => x.order_id === updated.order_id ? updated : x));
            setDetail(updated);
          }}
        />
      )}
    </div>
  );
}

function CategoriesAdmin() {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);

  const load = async () => {
    const [c, p] = await Promise.all([
      axios.get(`${API}/categories`),
      axios.get(`${API}/products?limit=200`),
    ]);
    setCats(c.data || []);
    setProducts(p.data?.items || []);
  };
  useEffect(() => { load(); }, []);

  const productCount = (slug) => products.filter(p => p.category === slug).length;

  const create = async () => {
    if (!name.trim()) { toast.error("Enter a category name"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/categories`, { name: name.trim(), slug: slug.trim() || undefined });
      toast.success(`Category "${name}" added`);
      setName(""); setSlug("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add category");
    } finally { setSaving(false); }
  };

  const rename = async (c) => {
    const nn = window.prompt(`Rename "${c.name}" to:`, c.name);
    if (!nn || nn.trim() === c.name) return;
    try {
      await axios.put(`${API}/admin/categories/${c.slug}`, { name: nn.trim() });
      toast.success("Renamed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rename failed");
    }
  };

  const remove = async (c) => {
    const used = productCount(c.slug);
    if (used > 0) {
      toast.error(`Cannot delete "${c.name}" — ${used} product(s) still use it`);
      return;
    }
    if (!window.confirm(`Delete category "${c.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/categories/${c.slug}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= cats.length) return;
    // Swap order values
    const a = cats[idx], b = cats[j];
    try {
      await Promise.all([
        axios.put(`${API}/admin/categories/${a.slug}`, { order: b.order }),
        axios.put(`${API}/admin/categories/${b.slug}`, { order: a.order }),
      ]);
      load();
    } catch (e) {
      toast.error("Reorder failed");
    }
  };

  return (
    <div data-testid="admin-categories">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-3xl text-[#2C2A29]">Categories</h2>
          <p className="text-sm text-[#8A8480] mt-1">Add or remove product categories. Changes reflect instantly across the storefront navigation and the product form.</p>
        </div>
      </div>

      {/* Create form */}
      <div className="bg-white border border-[#EAE3D9] rounded-2xl p-5 mb-6">
        <div className="label-small text-[#8A8480] mb-3">Add new category</div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3">
          <div>
            <Label className="text-xs">Display name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Baju Bandh"
              data-testid="cat-name-input"
            />
          </div>
          <div>
            <Label className="text-xs">URL slug (optional)</Label>
            <Input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="auto from name"
              data-testid="cat-slug-input"
            />
          </div>
          <div className="flex items-end">
            <Button onClick={create} disabled={saving} className="btn-primary w-full sm:w-auto" data-testid="cat-add-btn">
              <Plus className="h-4 w-4 mr-2" /> {saving ? "Adding…" : "Add category"}
            </Button>
          </div>
        </div>
        <p className="text-[11px] text-[#8A8480] mt-2">
          Slug is used in URLs (e.g. <code>/products/baju-bandh</code>). Leave empty to auto-generate from the name.
        </p>
      </div>

      {/* List */}
      <div className="bg-white border border-[#EAE3D9] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FDFBF7] text-[#8A8480]">
              <tr>
                <th className="text-left px-4 py-3 font-medium w-16">Order</th>
                <th className="text-left px-4 py-3 font-medium">Name</th>
                <th className="text-left px-4 py-3 font-medium">Slug</th>
                <th className="text-left px-4 py-3 font-medium">Products</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {cats.map((c, i) => {
                const count = productCount(c.slug);
                return (
                  <tr key={c.slug} className="border-t border-[#EAE3D9] hover:bg-[#FDFBF7]/40" data-testid={`cat-row-${c.slug}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => move(i, -1)} disabled={i === 0}
                          className="h-7 w-7 rounded-md border border-[#EAE3D9] text-xs text-[#595552] disabled:opacity-30 hover:border-[#C5A059]"
                          data-testid={`cat-up-${c.slug}`}>↑</button>
                        <button onClick={() => move(i, 1)} disabled={i === cats.length - 1}
                          className="h-7 w-7 rounded-md border border-[#EAE3D9] text-xs text-[#595552] disabled:opacity-30 hover:border-[#C5A059]"
                          data-testid={`cat-down-${c.slug}`}>↓</button>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium text-[#2C2A29]">{c.name}</td>
                    <td className="px-4 py-3">
                      <code className="text-xs bg-[#FFF5F3] px-2 py-0.5 rounded text-[#8B1E28]">{c.slug}</code>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${count > 0 ? "bg-emerald-50 text-emerald-700" : "bg-[#FDFBF7] text-[#8A8480]"}`}>
                        {count} product{count === 1 ? "" : "s"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => rename(c)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#EAE3D9] text-xs text-[#595552] hover:border-[#C5A059] hover:text-[#8B1E28] mr-2"
                        data-testid={`cat-rename-${c.slug}`}>
                        <Edit className="h-3.5 w-3.5" /> Rename
                      </button>
                      <button onClick={() => remove(c)} disabled={count > 0}
                        title={count > 0 ? "Move or delete these products first" : "Delete"}
                        className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs ${
                          count > 0
                            ? "border-[#EAE3D9] text-[#B8B0AA] cursor-not-allowed"
                            : "border-red-200 text-red-600 hover:bg-red-50"
                        }`}
                        data-testid={`cat-delete-${c.slug}`}>
                        <Trash2 className="h-3.5 w-3.5" /> Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
              {cats.length === 0 && (
                <tr><td colSpan="5" className="px-4 py-10 text-center text-[#8A8480]">No categories yet — add one above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function HomeCarouselAdmin() {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [initial, setInitial] = useState([]);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");

  const load = async () => {
    const [pRes, cRes] = await Promise.all([
      axios.get(`${API}/products?limit=200`),
      axios.get(`${API}/home-carousel`),
    ]);
    setProducts(pRes.data?.items || []);
    const ids = (cRes.data.products || []).map(p => p.product_id);
    setSelected(ids);
    setInitial(ids);
  };
  useEffect(() => { load(); }, []);

  const toggle = (pid) => {
    setSelected(prev => {
      if (prev.includes(pid)) return prev.filter(x => x !== pid);
      if (prev.length >= 8) { toast.error("Max 8 products in the carousel"); return prev; }
      return [...prev, pid];
    });
  };

  const move = (idx, dir) => {
    const arr = [...selected];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setSelected(arr);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/home-carousel`, { product_ids: selected });
      toast.success(`Home carousel updated (${selected.length} pieces)`);
      setInitial([...selected]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const dirty = JSON.stringify(selected) !== JSON.stringify(initial);
  const goldOnly = products
    .filter(p => p.purity !== "Silver")
    .filter(p => !query || p.name.toLowerCase().includes(query.toLowerCase()));
  const selectedProducts = selected.map(pid => products.find(p => p.product_id === pid)).filter(Boolean);

  return (
    <div data-testid="admin-home-carousel">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-3xl text-[#2C2A29]">Home Cinematic Carousel</h2>
          <p className="text-sm text-[#8A8480] mt-1">Choose up to 8 gold pieces to feature on the homepage cinematic reel. Reorder to control appearance sequence.</p>
        </div>
        <Button onClick={save} disabled={saving || !dirty} className="btn-primary" data-testid="carousel-save-btn">
          {saving ? "Saving…" : `Save (${selected.length}/8)`}
        </Button>
      </div>

      {/* Selected list — ordered */}
      <div className="bg-white border border-[#EAE3D9] rounded-2xl p-5 mb-6">
        <div className="label-small text-[#8A8480] mb-3">Selected · in display order</div>
        {selectedProducts.length === 0 ? (
          <div className="text-sm text-[#8A8480] py-6 text-center">No pieces selected yet. Pick from the catalogue below.</div>
        ) : (
          <div className="space-y-2">
            {selectedProducts.map((p, i) => (
              <div key={p.product_id} className="flex items-center gap-3 p-2 rounded-lg border border-[#EAE3D9] bg-[#FDFBF7]" data-testid={`selected-${p.product_id}`}>
                <span className="font-mono text-xs text-[#8A8480] w-6 text-center">{i + 1}</span>
                <img src={p.images?.[0]} alt="" className="h-12 w-12 rounded-lg object-cover" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-[#2C2A29] line-clamp-1">{p.name}</div>
                  <div className="text-xs text-[#8A8480]">{p.purity} · {p.weight}g · {inr(p.price)}</div>
                </div>
                <button onClick={() => move(i, -1)} disabled={i === 0}
                  className="h-8 w-8 rounded-md border border-[#EAE3D9] text-xs text-[#595552] disabled:opacity-30" data-testid={`move-up-${p.product_id}`}>↑</button>
                <button onClick={() => move(i, 1)} disabled={i === selectedProducts.length - 1}
                  className="h-8 w-8 rounded-md border border-[#EAE3D9] text-xs text-[#595552] disabled:opacity-30" data-testid={`move-down-${p.product_id}`}>↓</button>
                <button onClick={() => toggle(p.product_id)}
                  className="h-8 w-8 rounded-md border border-red-200 text-red-600 hover:bg-red-50" data-testid={`remove-${p.product_id}`}>
                  <X className="h-4 w-4 mx-auto" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Catalogue */}
      <div className="bg-white border border-[#EAE3D9] rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="label-small text-[#8A8480]">Catalogue · gold pieces only</div>
          <Input placeholder="Search products…" value={query} onChange={e => setQuery(e.target.value)}
            className="max-w-xs" data-testid="carousel-search" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {goldOnly.map(p => {
            const chosen = selected.includes(p.product_id);
            return (
              <button key={p.product_id} onClick={() => toggle(p.product_id)}
                className={`text-left rounded-xl overflow-hidden border-2 transition-all ${
                  chosen ? "border-[#8B1E28] ring-2 ring-[#8B1E28]/20" : "border-[#EAE3D9] hover:border-[#C5A059]"
                }`}
                data-testid={`toggle-${p.product_id}`}>
                <div className="relative aspect-square bg-[#FFF5F3]">
                  <img src={p.images?.[0]} alt="" className="w-full h-full object-cover" />
                  {chosen && (
                    <div className="absolute top-2 right-2 h-6 w-6 rounded-full bg-[#8B1E28] text-white text-xs flex items-center justify-center">
                      <CheckCircle2 className="h-4 w-4" />
                    </div>
                  )}
                </div>
                <div className="p-2 text-xs">
                  <div className="font-medium text-[#2C2A29] line-clamp-1">{p.name}</div>
                  <div className="text-[#8A8480]">{p.purity} · {inr(p.price)}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ReviewsAdmin() {
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/reviews`, { params: filter === "all" ? {} : { status: filter } });
      setReviews(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const moderate = async (id, action) => {
    try {
      await axios.patch(`${API}/admin/reviews/${id}`, { action });
      toast.success(action === "approve" ? "Review approved" : "Review rejected");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update review");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`);
      toast.success("Review deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete review");
    }
  };

  const filters = [
    { key: "pending", label: "Pending" },
    { key: "approved", label: "Approved" },
    { key: "rejected", label: "Rejected" },
    { key: "all", label: "All" },
  ];

  const [sendingReminders, setSendingReminders] = useState(false);
  const sendReminders = async () => {
    if (!window.confirm("Send review-request emails now to every delivered order (older than 7 days) that hasn't been reminded yet?")) return;
    setSendingReminders(true);
    try {
      const r = await axios.post(`${API}/admin/reviews/send-reminders`);
      toast.success(`Sent ${r.data.sent} reminder${r.data.sent === 1 ? "" : "s"}${r.data.failed ? ` · ${r.data.failed} failed` : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send reminders");
    } finally {
      setSendingReminders(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
  };

  return (
    <div data-testid="admin-reviews">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h2 className="font-display text-3xl text-[#2C2A29] mb-2">Customer Reviews</h2>
          <p className="text-sm text-[#8A8480]">Approve reviews to make them visible on product pages.</p>
        </div>
        <Button
          onClick={sendReminders}
          disabled={sendingReminders}
          variant="outline"
          className="rounded-full border-[#C5A059] text-[#8B1E28] hover:bg-[#FFF5F3]"
          data-testid="send-review-reminders-btn"
        >
          {sendingReminders ? "Sending…" : "Send review reminders now"}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="reviews-filter-bar">
        {filters.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-full text-sm border transition-colors ${filter === f.key ? "bg-[#8B1E28] text-white border-[#8B1E28]" : "bg-white border-[#EAE3D9] text-[#595552] hover:border-[#C5A059]"}`}
            data-testid={`reviews-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#8A8480]">Loading…</div>
      ) : reviews.length === 0 ? (
        <div className="bg-white border border-[#EAE3D9] rounded-2xl p-10 text-center text-[#8A8480]" data-testid="reviews-empty">
          No {filter === "all" ? "" : filter} reviews yet.
        </div>
      ) : (
        <div className="grid gap-4" data-testid="admin-reviews-list">
          {reviews.map(r => (
            <div key={r.review_id} className="bg-white border border-[#EAE3D9] rounded-2xl p-5" data-testid={`admin-review-${r.review_id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {r.product_image ? (
                    <img src={r.product_image} alt="" className="h-14 w-14 rounded-xl object-cover border border-[#EAE3D9]" />
                  ) : (
                    <div className="h-14 w-14 rounded-xl bg-[#FFF5F3]" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-xs label-small text-[#C5A059]">{r.product_name}</div>
                    <div className="mt-1 flex items-center gap-2">
                      <div className="flex">
                        {[1,2,3,4,5].map(n => (
                          <Star key={n} className={`h-4 w-4 ${n <= (r.rating || 0) ? "fill-[#C5A059] text-[#C5A059]" : "text-[#D6CFC4]"}`} />
                        ))}
                      </div>
                      <span className={`label-small px-2 py-0.5 rounded-full ${r.status === "approved" ? "bg-emerald-50 text-emerald-700" : r.status === "rejected" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
                        {r.status}
                      </span>
                      {r.is_verified_buyer && (
                        <span className="label-small px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" title="Bought this exact piece">
                          ✓ verified buyer
                        </span>
                      )}
                    </div>
                    {r.title && <div className="mt-2 font-semibold text-[#2C2A29]">{r.title}</div>}
                    <p className="mt-1 text-sm text-[#595552] whitespace-pre-line">{r.body}</p>
                    {(r.images || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2" data-testid={`admin-review-${r.review_id}-photos`}>
                        {r.images.map((src, i) => (
                          <a key={i} href={src} target="_blank" rel="noopener noreferrer"
                            className="block h-16 w-16 rounded-lg overflow-hidden border border-[#EAE3D9] hover:border-[#C5A059] transition-colors">
                            <img src={src} alt="" className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    )}
                    <div className="mt-2 text-xs text-[#8A8480]">
                      By <span className="text-[#2C2A29]">{r.reviewer_name || "Anonymous"}</span>
                      {r.reviewer_email ? <> · {r.reviewer_email}</> : null}
                      {" "}· {fmtDate(r.created_at)}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  {r.status !== "approved" && (
                    <Button size="sm" onClick={() => moderate(r.review_id, "approve")}
                      className="btn-primary rounded-full" data-testid={`review-approve-${r.review_id}`}>
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                    </Button>
                  )}
                  {r.status !== "rejected" && (
                    <Button size="sm" variant="outline" onClick={() => moderate(r.review_id, "reject")}
                      className="rounded-full border-[#EAE3D9] text-[#595552]" data-testid={`review-reject-${r.review_id}`}>
                      <X className="h-4 w-4 mr-1" /> Reject
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => remove(r.review_id)}
                    className="rounded-full text-red-600 hover:bg-red-50" data-testid={`review-delete-${r.review_id}`}>
                    <Trash2 className="h-4 w-4 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UsersAdmin() {
  const [users, setUsers] = useState([]);
  useEffect(() => { axios.get(`${API}/admin/users`).then(r => setUsers(r.data)); }, []);
  return (
    <div data-testid="admin-users">
      <h2 className="font-display text-3xl text-[#2C2A29] mb-6">Users</h2>
      <div className="bg-white border border-[#EAE3D9] rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FDFBF7] text-[#8A8480]">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Name</th>
              <th className="text-left px-4 py-3 font-medium">Email</th>
              <th className="text-left px-4 py-3 font-medium">Role</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.user_id} className="border-t border-[#EAE3D9]">
                <td className="px-4 py-3 flex items-center gap-3">
                  {u.picture ? <img src={u.picture} alt="" className="h-8 w-8 rounded-full" /> : <div className="h-8 w-8 rounded-full bg-[#8B1E28] text-white flex items-center justify-center text-xs">{u.name?.[0]}</div>}
                  {u.name}
                </td>
                <td className="px-4 py-3">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`label-small px-3 py-1 rounded-full ${u.role === "admin" ? "bg-[#FFF5F3] text-[#8B1E28]" : "bg-[#FDFBF7] text-[#595552]"}`}>{u.role}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) { login(); return; }
    if (user.role !== "admin") { toast.error("Admin access required"); navigate("/"); }
  }, [user, loading, login, navigate]);

  if (!user || user.role !== "admin") return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-page">
      <div className="mb-8">
        <p className="label-small text-[#C5A059] mb-2">Sumanya · Admin</p>
        <h1 className="font-display text-4xl text-[#2C2A29]">Welcome back, {user.name?.split(" ")[0]}</h1>
      </div>
      <div className="flex flex-col lg:flex-row gap-8">
        <AdminSidebar />
        <div className="flex-1 min-w-0">
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="gold-rate" element={<GoldRateAdmin />} />
            <Route path="categories" element={<CategoriesAdmin />} />
            <Route path="products" element={<ProductsAdmin />} />
            <Route path="bulk-upload" element={<BulkUploadAdmin />} />
            <Route path="home-carousel" element={<HomeCarouselAdmin />} />
            <Route path="orders" element={<OrdersAdmin />} />
            <Route path="reviews" element={<ReviewsAdmin />} />
            <Route path="users" element={<UsersAdmin />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}
